import DeleteIconButton from '../components/DeleteIconButton';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, FormControl, FormControlLabel, FormHelperText, Grid, Hidden, InputLabel, LinearProgress, Select, Switch, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { CREATE, DataAPIContext, DELETE, UPDATE } from '../api';
import { ContextStore } from '../store/contextStore';
import { fromServerErrors, validate } from '../utils';
import { checkAndAddHttpPrefix } from '../utils/http_prefix';
import { REVIEW_ENTRY_TYPE_TOTALS, REVIEW_SOURCE_OVERALL, reviewSourcesMenuItems } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {},
}));

const validateReview = (review) => {
    const errors = {};
    let canSave = true;
    if (!review.review_source_url || review.review_source_url === '') {
        canSave = false;
    }
    return { canSave, errors };
};

const ActivityReview = (props) => {
    const TEXT = 1;
    const CHECKBOX = 2;
    const SELECT = 3;
    const reviewSourceRef = React.useRef(null);
    const showReviewsRef = React.useRef(null);
    const starRatingRef = React.useRef(null);
    const reviewCountRef = React.useRef(null);
    const sourceLinkRef = React.useRef(null);
    const review = props.data;

    const [reviewSourceLink, setReviewSourceLink] = React.useState(props.data.review_source_url ? props.data.review_source_url : '');

    const valres = validate(props.data, props.data.errors, validateReview);

    const getValue = (target, type) => {
        if (type === TEXT) {
            return target.value;
        } else if (type === CHECKBOX) {
            return target.checked;
        } else if (type === SELECT) {
            return target.value;
        }
    };
    const genOnChange = (name, type) => {
        return async (ev) => {
            const val = getValue(ev.target, type);
            const newReview = { ...review };
            newReview[name] = val;
            //Prevents server errors 400 and 406, fields expect correct types and min value
            if (ev.target.name === 'review_count_overall') {
                newReview[name] = val ? parseInt(val) : 0;
            }
            if (ev.target.name === 'star_rating_overall') {
                newReview[name] = val ? parseFloat(val) : 1.0;
            }

            if (newReview.errors && newReview.errors[name]) {
                delete newReview.errors[name];
            }

            (await props.onChange) && props.onChange(newReview, props.idx, ev);
        };
    };

    const [missingURLError, setMissingURLError] = React.useState(false);
    const [firstRender, setFirstRender] = React.useState(false);
    const onBlur = (ev) => {
        if (!ev.target.value && ev.target.name !== 'review_source_url' && ev.target.name !== 'star_rating_overall' && ev.target.name !== 'review_count_overall') {
            return;
        }

        if (!firstRender && (!sourceLinkRef.current.value || sourceLinkRef.current.value === '')) {
            setMissingURLError(true);
            return;
        }

        if (!valres.is_valid) {
            setFirstRender(false);
            return;
        }

        setMissingURLError(false);
        setFirstRender(false);

        setTimeout(() => {
            const a = document.activeElement;
            const hasFocus = a === reviewSourceRef.current || a === showReviewsRef.current || a === starRatingRef.current || a === reviewCountRef.current || a === sourceLinkRef.current;
            if (!hasFocus) {
                props.onBlur && props.onBlur(props.idx);
            }
        }, 10); // wait for focus TODO: better ideas?
    };

    const handleReviewSourceLinkChange = async (ev) => {
        setReviewSourceLink(ev.target.value);

        if (ev.target.name === 'review_source_url') {
            let name = 'review_source_url';
            const newReview = { ...review };
            newReview[name] = ev.target.value;
            (await props.onChange) && props.onChange(newReview, props.idx, ev);
        }
    };

    React.useEffect(() => {
        if (review.dirty) {
            props.onBlur && props.onBlur(props.idx);
        }
    }, [review.include_in_aggregate]);

    return (
        <Box my={2} onBlur={onBlur}>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <LinearProgress color="primary" style={{ visibility: review.uploading ? 'visible' : 'hidden' }} />
                </Grid>
                <Grid item xs={10} sm={2}>
                    <FormControl variant="filled" size="small" fullWidth disabled={props.disabled}>
                        <InputLabel>Review Source</InputLabel>
                        <Select {...valres.muiAttrs('review_source_name', true)} value={props.data.review_source_name} onChange={genOnChange('review_source_name', SELECT)} ref={reviewSourceRef}>
                            {reviewSourcesMenuItems}
                        </Select>
                        {valres.hasError('review_source_name') ? <FormHelperText>{valres.errorText('review_source_name')}</FormHelperText> : null}
                    </FormControl>
                </Grid>
                <Hidden smDown>
                    <Grid item xs={5} md={9}>
                        <FormControl {...valres.muiAttrs('include_in_aggregate', true)} disabled={props.disabled}>
                            <FormControlLabel control={<Switch checked={props.data.include_in_aggregate} onChange={genOnChange('include_in_aggregate', CHECKBOX)} name="checkedB" color="primary" inputRef={showReviewsRef} />} label="Show Reviews" />
                            {valres.hasError('include_in_aggregate') ? <FormHelperText>{valres.errorText('include_in_aggregate')}</FormHelperText> : null}
                        </FormControl>
                    </Grid>
                </Hidden>
                <Grid item xs={2} md={1} container alignItems="center" justifyContent="flex-end">
                    <DeleteIconButton title="Delete review" tabIndex="-1" onClick={(ev) => props.onDelete && props.onDelete(props.data, props.idx, ev)} disabled={props.disabled} />
                </Grid>
                <Hidden mdUp>
                    <Grid item xs={12}>
                        <FormControlLabel disabled={props.disabled} control={<Switch checked={props.data.include_in_aggregate} onChange={genOnChange('include_in_aggregate', CHECKBOX)} name="checkedB" color="primary" inputRef={showReviewsRef} />} label="Show Reviews" {...valres.muiAttrs('include_in_aggregate')} />
                    </Grid>
                </Hidden>
                <Grid item xs={6} md={2}>
                    <TextField
                        {...valres.muiAttrs('star_rating')}
                        variant="filled"
                        label="Star Rating"
                        size="small"
                        name="star_rating_overall"
                        value={props.data.star_rating || 1}
                        type="number"
                        disabled={props.disabled}
                        fullWidth
                        InputProps={{
                            inputRef: starRatingRef,
                            inputProps: { min: 1, max: 5, step: 0.5 },
                        }}
                        onChange={genOnChange('star_rating', TEXT)}
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <TextField
                        {...valres.muiAttrs('review_count')}
                        variant="filled"
                        label="Review Count"
                        size="small"
                        name="review_count_overall"
                        value={props.data.review_count || 0}
                        type="number"
                        disabled={props.disabled}
                        fullWidth
                        InputProps={{
                            inputRef: reviewCountRef,
                            inputProps: { min: 0, step: 1 },
                        }}
                        onChange={genOnChange('review_count', TEXT)}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField
                        error={missingURLError}
                        helperText={missingURLError && 'URL is required.'}
                        {...valres.muiAttrs('review_source_url')}
                        variant="filled"
                        disabled={props.disabled}
                        label="Review Source Link"
                        name="review_source_url"
                        size="small"
                        value={reviewSourceLink}
                        type="url"
                        inputRef={sourceLinkRef}
                        fullWidth
                        required
                        onChange={handleReviewSourceLinkChange}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

const ActivityReviews = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const [reviews, setReviews] = React.useState(props.data || []);
    let mounted = React.useRef(true);
    const { enqueueSnackbar } = useSnackbar();

    const emptyReview = {
        date_last_updated: null,
        entry_type: REVIEW_ENTRY_TYPE_TOTALS,
        id: null,
        include_in_aggregate: true,
        pcts_instead_of_counts: false,
        review_count_1_star: 0,
        review_count_2_star: 0,
        review_count_3_star: 0,
        review_count_4_star: 0,
        review_count_5_star: 0,
        review_count: 0,
        review_source_name: REVIEW_SOURCE_OVERALL,
        review_source_url: '',
        star_rating: 1,
    };

    const onAdd = (ev) => {
        const newReviews = [...reviews, emptyReview];
        console.log('onAdd: newReviews=', newReviews);
        setReviews(newReviews);
        props.onChange && props.onChange(newReviews);
    };

    const updateReviewState = (review, idx) => {
        if (!mounted.current) {
            return;
        }
        console.log('updateReviewState:reviews[', idx, ']: update =', review, reviews);
        let newReviews = reviews.map((e, i) => (i === idx ? review : e));
        console.log('updateReviewState:reviews[', idx, ']: new reviews list =', newReviews);
        setReviews(newReviews);
        props.onChange && props.onChange(newReviews);
    };

    const onChange = (review, idx) => updateReviewState({ ...review, dirty: true, errors: null }, idx);

    const onBlur = (idx) => {
        if (reviews[idx].dirty) {
            console.log('parent.onBlur:reviews[', idx, ']:', reviews[idx]);
            const ACTION = reviews[idx].id ? UPDATE : CREATE;
            const params = { id: reviews[idx].id, data: { ...reviews[idx], ...props.resourceMeta } };
            params.data.review_source_url = checkAndAddHttpPrefix(params.data.review_source_url);
            dataAPI(ACTION, props.resource, params).then((res) => {
                if (res.statusCode === 406) {
                    const se = fromServerErrors(res.data);
                    console.log('server errors', res.data, se);
                    updateReviewState({ ...reviews[idx], errors: fromServerErrors(res.data), uploading: false }, idx);
                    // enqueueSnackbar("Validation failed", { variant: 'warning' });
                } else if (res.statusCode === 200 || res.statusCode === 201) {
                    updateReviewState(res.data, idx);
                    enqueueSnackbar('Review saved', { variant: 'success' });
                } else {
                    enqueueSnackbar('Unexpected server response: ' + res.statusCode, { variant: 'error' });
                }
            });
            updateReviewState({ ...reviews[idx], uploading: true }, idx);
        }
    };

    const onReviewDeleted = (idx) => {
        if (!mounted.current) {
            return;
        }
        const newReviews = reviews.filter((e, i) => i !== idx);
        setReviews(newReviews);
        props.onChange && props.onChange(newReviews);
    };

    const onDelete = (review, idx) => {
        updateReviewState({ ...review, dirty: true }, idx);
        dataAPI(DELETE, props.resource, { id: review.id }).then((res) => {
            onReviewDeleted(idx);
            enqueueSnackbar('Review deleted', { variant: 'default' });
        });
    };

    const existing = {};
    reviews.forEach((r) => {
        if (!existing[r.review_source_name]) {
            existing[r.review_source_name] = 0;
        }
        existing[r.review_source_name] += 1;
    });

    // const hasMoreToAdd = !compareObjectKeys(existing, REVIEW_SOURCES);
    const hasMoreToAdd = reviews.length === 0;

    // console.log('existing', existing, hasMoreToAdd);
    // const reviewSourceItems = {};
    // Object.keys(REVIEW_SOURCES).filter(rs => !existing[rs]).forEach(k => {
    //     reviewSourceItems[k] = REVIEW_SOURCES[k];
    // });
    // console.log('keys', reviewSourceItems);

    return (
        <div className={classes.root}>
            <Grid container direction="row">
                {reviews.map((e, idx) => (
                    <Grid item xs={12} key={idx}>
                        <ActivityReview disabled={props.disabled} data={e} idx={idx} onChange={onChange} onDelete={onDelete} onBlur={onBlur} existing={existing} isSmallScreen={props.isSmallScreen} />
                        {idx < reviews.length - 1 ? <Divider /> : null}
                    </Grid>
                ))}

                {hasMoreToAdd ? (
                    <Grid item xs={12} container justifyContent="flex-end">
                        <Button disabled={!hasPerm('activity', 'write')} variant="outlined" color="primary" onClick={onAdd} >
                            <AddIcon fontSize="small"/>
                            &nbsp;Add review
                        </Button>
                    </Grid>
                ) : null}
            </Grid>
        </div>
    );
};

export default ActivityReviews;
